import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthManager from './components/AuthManager';
import Header from './components/Header';
import Footer from './components/Footer';
import GamePage from './components/GamePage';
import StatisticsPage from './components/StatisticsPage';
import TeacherDashboard from './components/TeacherDashboard';
import JoinClass from './components/JoinClass';
import ClassDetails from './components/ClassDetails';
import AboutPage from './components/AboutPage';
import SEOHead from './components/SEOHead';
import './App.css';
import './styles/header.css';
import './styles/wordleGrid.css';
import './styles/footer.css';
import './styles/statistics.css';

function App() {
  const [user, setUser] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Check for existing session on app load
  useEffect(() => {
    const checkExistingSession = async () => {
      const token = localStorage.getItem('aminole_token');
      const userStr = localStorage.getItem('aminole_user');
      const tokenExpiry = localStorage.getItem('aminole_token_expiry');
      
      if (token && userStr && tokenExpiry) {
        try {
          const user = JSON.parse(userStr);
          const expiryTime = parseInt(tokenExpiry);
          const now = Date.now();
          
          // Check if token is still valid (within 24 hours)
          if (now < expiryTime) {
            // Token is still valid, restore user session
            setUser(user);
          } else {
            // Token expired, clear storage
            localStorage.removeItem('aminole_token');
            localStorage.removeItem('aminole_user');
            localStorage.removeItem('aminole_token_expiry');
          }
        } catch (error) {
          // Invalid stored data, clear it
          localStorage.removeItem('aminole_token');
          localStorage.removeItem('aminole_user');
          localStorage.removeItem('aminole_token_expiry');
        }
      }
      setIsInitializing(false);
    };
    
    checkExistingSession();
  }, []);

  const handleAuthSuccess = (userData) => {
    // Set token expiry to 24 hours from now
    const expiryTime = Date.now() + (24 * 60 * 60 * 1000); // 24 hours in milliseconds
    localStorage.setItem('aminole_token_expiry', expiryTime.toString());
    
    setUser(userData);
  };

  const handleLoginClick = () => {
    // Login is now handled by routing to /auth
  };

  const handleLogout = () => {
    localStorage.removeItem('aminole_token');
    localStorage.removeItem('aminole_user');
    localStorage.removeItem('aminole_token_expiry');
    setUser(null);
  };

  // Show loading screen while checking for existing session
  if (isInitializing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #00bcd4 0%, #00acc1 100%)',
        color: 'white',
        fontSize: '18px',
        fontWeight: '600',
        fontFamily: 'Comfortaa, Arial, sans-serif'
      }}>
        Restoring your session...
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <SEOHead />
        <Header 
          user={user} 
          onLoginClick={handleLoginClick}
          onLogout={handleLogout}
        />
        
        <Routes>
          <Route 
            path="/" 
            element={<GamePage user={user} />} 
          />
          <Route 
            path="/game" 
            element={<GamePage user={user} />} 
          />
          <Route path="/statistics" element={<StatisticsPage user={user} />} />
          <Route path="/teacher-dashboard" element={<TeacherDashboard user={user} />} />
          <Route path="/join-class" element={<JoinClass user={user} />} />
          <Route path="/class/:classId" element={<ClassDetails user={user} />} />
          <Route path="/about" element={<AboutPage />} />
          <Route 
            path="/auth" 
            element={
              user ? (
                <Navigate to="/game" replace />
              ) : (
                <AuthManager onAuthSuccess={handleAuthSuccess} />
              )
            } 
          />
          <Route 
            path="/reset-password" 
            element={<AuthManager onAuthSuccess={handleAuthSuccess} />} 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        <Footer />
      </div>
    </Router>
  );
}

export default App;
